import Box from '@mui/material/Box';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import { alpha, useTheme } from '@mui/material/styles';
import {
  CategoryScale,
  Chart as ChartJS,
  Filler,
  Legend,
  LineElement,
  LinearScale,
  PointElement,
  Tooltip,
} from 'chart.js';
import { MouseEvent, useMemo, useState } from 'react';
import { Line } from 'react-chartjs-2';
import { useTranslation } from 'react-i18next';

import { buildSignupTimelineSeries } from 'src/helpers/signupTimeline';
import {
  SignupTimelineMode,
  SignupTimelinePoint,
} from 'src/interfaces/SignupTimeline';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
);

interface Props {
  points: SignupTimelinePoint[];
  /** Controlled mode; when omitted, component owns the toggle. */
  mode?: SignupTimelineMode;
  onModeChange?: (mode: SignupTimelineMode) => void;
  height?: number;
}

const SignupTimelineChart = ({
  points,
  mode: controlledMode,
  onModeChange,
  height = 280,
}: Props) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [localMode, setLocalMode] = useState<SignupTimelineMode>('cumulative');
  const mode = controlledMode ?? localMode;

  const series = useMemo(() => buildSignupTimelineSeries(points), [points]);
  const values = mode === 'daily' ? series.daily : series.cumulative;

  if (series.labels.length === 0) {
    return null;
  }

  const axisColor = theme.palette.text.secondary;
  const gridColor = alpha(theme.palette.text.primary, 0.12);
  const lineColor = theme.palette.primary.main;

  const handleModeChange = (
    _event: MouseEvent<HTMLElement>,
    next: SignupTimelineMode | null,
  ) => {
    if (!next) return;
    if (onModeChange) {
      onModeChange(next);
    } else {
      setLocalMode(next);
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={mode}
          onChange={handleModeChange}
          aria-label={t('signups.timeline.modeLabel')}
        >
          <ToggleButton value="daily">{t('signups.timeline.daily')}</ToggleButton>
          <ToggleButton value="cumulative">{t('signups.timeline.cumulative')}</ToggleButton>
        </ToggleButtonGroup>
      </Box>
      <Box sx={{ height, width: '100%' }}>
        <Line
          options={{
            maintainAspectRatio: false,
            responsive: true,
            plugins: {
              legend: { display: false },
              tooltip: {
                displayColors: false,
              },
            },
            scales: {
              x: {
                ticks: {
                  color: axisColor,
                  maxRotation: 45,
                  autoSkip: true,
                  maxTicksLimit: 12,
                },
                grid: { color: gridColor },
              },
              y: {
                beginAtZero: true,
                ticks: {
                  color: axisColor,
                  precision: 0,
                },
                grid: { color: gridColor },
              },
            },
          }}
          data={{
            labels: series.labels,
            datasets: [
              {
                label:
                  mode === 'daily'
                    ? t('signups.timeline.daily')
                    : t('signups.timeline.cumulative'),
                data: values,
                borderColor: lineColor,
                backgroundColor: alpha(lineColor, 0.16),
                fill: true,
                tension: 0.25,
                pointRadius: series.labels.length > 40 ? 0 : 3,
                pointHoverRadius: 5,
              },
            ],
          }}
        />
      </Box>
    </Box>
  );
};

export default SignupTimelineChart;
